/**
 * Help centre FAQ content.
 *
 * Written for the people using the portal, not for us: every answer says
 * what to do and where, in the words on the screen. No roadmap or ticket
 * ids, no internal system names, no history of how something used to work —
 * `faqContent.test.ts` fails the build if any of that creeps back in.
 *
 * Shape: `a` is the short answer (2–4 sentences) everyone reads; `more` is
 * optional detail behind a "More detail" toggle. `**bold**` renders bold.
 *
 * Audience: an entry shows to anyone holding one of its roles in any tenant
 * (or everyone, for 'all'). Staff work is usually ['tenant_admin',
 * 'coordinator']; admin-only settings are ['tenant_admin'].
 */
import type { Role } from '../../api/users';

export type Audience = Role | 'all';

export interface FaqEntry {
  /** Stable id — anchors, tests and links point at it. */
  id: string;
  section: FaqSection;
  audience: Audience[];
  q: string;
  a: string;
  more?: string;
}

/** Sections in reading order: from first sign-in to platform operations. */
export const FAQ_SECTIONS = [
  'Getting started',
  'Signing in & security',
  'Recording a session',
  'After a session',
  'Your results',
  'Analysis & reports',
  'Live',
  'Recordings & activity',
  'Participants',
  'Devices',
  'Cameras & edge appliances',
  'Tenant administration',
  'Platform administration',
] as const;

export type FaqSection = (typeof FAQ_SECTIONS)[number];

const STAFF: Audience[] = ['tenant_admin', 'coordinator'];
const ADMIN: Audience[] = ['tenant_admin'];
const SUPER: Audience[] = ['superadmin'];
const PARTICIPANT: Audience[] = ['participant'];
const TENANT_ALL: Audience[] = ['tenant_admin', 'coordinator', 'participant'];

export const FAQS: FaqEntry[] = [
  // ── Getting started ────────────────────────────────────────────────────
  {
    id: 'what-is-tenant',
    section: 'Getting started',
    audience: ['all'],
    q: 'What is a tenant?',
    a: 'A tenant is an organisation using 10NEWTONS — a research group, a training programme or a hospital department. Each tenant has its own administrators and participants, and its data is kept separate from every other tenant.',
  },
  {
    id: 'roles',
    section: 'Getting started',
    audience: ['all'],
    q: 'What can each role do?',
    a: '**Tenant admins** run their organisation: participants, recordings, devices, cameras, settings and staff. **Coordinators** run the day-to-day work but not settings, staff or the procedure catalogue. **Participants** are the people being recorded; they see their own results and the cases they took part in. **Platform admins** are 10NEWTONS staff who set up and support tenants.',
    more: 'A tenant admin can also be made **read-only**: they see everything in the tenant but cannot change or download anything — the right grant for an observer or auditor.',
  },
  {
    id: 'coordinator-limits',
    section: 'Getting started',
    audience: ['coordinator'],
    q: "I'm a coordinator — what can't I do?",
    a: 'You can manage participants, recordings, procedure instances, attachments and devices. Changing tenant settings, managing staff, editing the procedure or baseline catalogue, managing departments and facilities, and retiring a device are reserved for tenant admins. Those pages are hidden or read-only for you — ask a tenant admin when you need one of them changed.',
  },
  {
    id: 'invitations',
    section: 'Getting started',
    audience: ['all'],
    q: 'How do invitations work?',
    a: 'An invitation is an email with a sign-in link. Click it: if you are new you choose a password and your account is created, already verified; if you already have an account, the new organisation is simply added to it. Invitation links are valid for 7 days.',
  },
  {
    id: 'multiple-tenants',
    section: 'Getting started',
    audience: ['all'],
    q: 'Can I belong to more than one organisation, and how do I switch?',
    a: 'Yes — each invitation adds a membership, and you can hold different roles in different organisations. When you belong to more than one, the organisation name in the top bar becomes a menu: pick another organisation and the portal switches to it.',
    more: 'Your choice of organisation is remembered in this browser only. Participants can also see every organisation\'s recordings together on their Dashboard.',
  },
  {
    id: 'first-sign-in-profile',
    section: 'Getting started',
    audience: PARTICIPANT,
    q: 'Why did my first sign-in open my Profile?',
    a: 'Only the very first time: we ask you to review your details and add your **job title** and **years of experience**. Every later sign-in opens your Dashboard.',
  },
  {
    id: 'job-title-experience',
    section: 'Getting started',
    audience: PARTICIPANT,
    q: 'Why do you ask for my job title and years of experience?',
    a: 'Your results are compared with reference groups of similar experience — a second-year resident and a twenty-year attending belong in different comparisons. Without these two fields the comparison cannot pick the right group. You can change them on your Profile at any time, and your tenant admin can fill them in for you.',
  },
  {
    id: 'contact-details',
    section: 'Getting started',
    audience: PARTICIPANT,
    q: 'How do I update my contact details?',
    a: 'Open **Profile** from the top-bar menu. Your name, phone number and address are yours to change. US clinicians can enter their 10-digit NPI and click **Look up** to fill in name, phone and practice address from the public registry — you can edit everything before saving.',
  },
  {
    id: 'who-sees-my-data',
    section: 'Getting started',
    audience: PARTICIPANT,
    q: 'Who can see my recordings?',
    a: "The admins and coordinators of the organisation you recorded with can see those recordings — not recordings you made with a different organisation. Teammates who recorded the same case at the same time can see that case in Analysis and on the Live page. 10NEWTONS staff do not browse tenant data.",
  },

  // ── Signing in & security ──────────────────────────────────────────────
  {
    id: 'terms-and-privacy',
    section: 'Signing in & security',
    audience: ['all'],
    q: 'Why am I asked to accept the Terms of Use and Privacy Policy?',
    a: "Creating an account or accepting an invitation asks you to tick that you have read and agree to the **Terms of Use** and the **Privacy Policy**; the links open them in a new tab. When either changes in a way that matters, you're asked once more after signing in. Both are always at **/terms** and **/privacy**, also linked under the sign-in form.",
    more: "We keep a record of which version you accepted and when. A small correction, such as a new contact address, applies without asking you again. If you'd rather not accept, choose **Sign out**; your organisation's admin can tell you more about how your recordings are used.",
  },
  {
    id: 'no-verification-email',
    section: 'Signing in & security',
    audience: ['all'],
    q: "I didn't receive my verification email.",
    a: "Check your spam or junk folder first — the email comes from 10NEWTONS. Verification links expire after 24 hours; if yours has, register again with the same address. If you were invited by an organisation, use the invitation email instead: it signs you in without a separate verification step.",
  },
  {
    id: 'forgot-password',
    section: 'Signing in & security',
    audience: ['all'],
    q: "I've forgotten my password.",
    a: "Click **Forgot password?** under the sign-in form and enter your email. You'll get a link, valid for one hour, to set a new password — saving it signs you straight in.",
    more: 'The page always says the email was sent, whether or not the address has an account, so nobody can use it to find out who is registered.',
  },
  {
    id: 'change-password',
    section: 'Signing in & security',
    audience: ['all'],
    q: 'How do I change my password?',
    a: "Go to **Profile → Change password**, enter your current password and the new one twice. If you've forgotten the current one, sign out and use **Forgot password?** instead.",
  },
  {
    id: 'password-rules',
    section: 'Signing in & security',
    audience: ['all'],
    q: 'What are the password rules?',
    a: 'At least 8 characters, with an uppercase letter, a lowercase letter, a digit and a special character (such as ! @ - #). Every screen that sets a password shows a checklist that ticks off each rule as you type.',
  },
  {
    id: 'session-length',
    section: 'Signing in & security',
    audience: ['all'],
    q: 'How long do I stay signed in?',
    a: 'You are signed out after 15 minutes without activity (a "Still there?" prompt gives you a minute to stay), when you close the browser, and at least once a day. The portal is used on shared machines, so sign-ins are kept short on purpose.',
  },
  {
    id: 'two-factor',
    section: 'Signing in & security',
    audience: ['superadmin', 'tenant_admin', 'coordinator'],
    q: 'How does two-factor authentication work?',
    a: 'When your organisation requires it, your next sign-in takes you through setup: scan the QR code with an authenticator app, enter the 6-digit code, and save the ten recovery codes you are shown. After that, every sign-in asks for a code. Participants can turn two-factor on for themselves on their Profile.',
  },
  {
    id: 'lost-authenticator',
    section: 'Signing in & security',
    audience: ['all'],
    q: "I've lost my authenticator app — how do I get back in?",
    a: 'On the code screen, choose **Lost access? Use a recovery code** and enter one of the codes you saved at setup (each works once). Then set up a new authenticator from your Profile. If you have no recovery codes left, contact 10NEWTONS support.',
  },
  {
    id: 'sso',
    section: 'Signing in & security',
    audience: ['all'],
    q: 'Can I sign in with my organisation login (single sign-on)?',
    a: "If your organisation has set it up with us, yes: type your work email and a **Sign in with your organization** button appears. Your account is created on first sign-in and your organisation's login handles the second factor. Until your IT team sets it up with 10NEWTONS, use email and password.",
  },
  {
    id: 'genuine-emails',
    section: 'Signing in & security',
    audience: ['all'],
    q: 'How do I know an email from 10NEWTONS is genuine?',
    a: 'Our emails come from 10NEWTONS, show our logo, link only to this portal, and print the link in full under the button. A genuine email never contains your results and never asks for your password.',
  },
  {
    id: 'light-dark',
    section: 'Signing in & security',
    audience: ['all'],
    q: 'How do I switch between light and dark mode?',
    a: 'Use the sun / moon icon in the top bar. "System" follows your computer\'s setting; the choice is remembered in this browser.',
  },
  {
    id: 'sort-and-search',
    section: 'Signing in & security',
    audience: ['all'],
    q: 'Can I sort a table or search a drop-down list?',
    a: 'Yes. Click any column heading to sort by it; click again to reverse the order. Drop-down lists are alphabetical — type a few letters to narrow them down, then pick with the mouse or Enter.',
  },

  // ── Recording a session ────────────────────────────────────────────────
  {
    id: 'phone-locked',
    section: 'Recording a session',
    audience: TENANT_ALL,
    q: 'Can the phone be locked or pocketed during a recording?',
    a: 'Yes. The capture app keeps recording with the screen off or the phone in a pocket. Keep it within about a metre of the hand sensors for the steadiest motion data.',
  },
  {
    id: 'headband-drop',
    section: 'Recording a session',
    audience: TENANT_ALL,
    q: 'What happens if a sensor briefly disconnects mid-recording?',
    a: 'The app reconnects and carries on; only the seconds it was out of reach are missing. The gap shows up in that recording\'s capture quality rather than stopping the session.',
  },
  {
    id: 'rate-check',
    section: 'Recording a session',
    audience: STAFF,
    q: 'Can the operator check sensor rates before recording?',
    a: 'Yes. On the capture app\'s Connect screen, **Test Sensor Rates** shows each sensor\'s live sampling rate against your minimum, without recording. Move the phone closer or re-seat the headband until every rate is green, then start the session.',
  },
  {
    id: 'minimum-rates',
    section: 'Recording a session',
    audience: STAFF,
    q: 'Can we stop operators from quietly recording poor data?',
    a: 'Yes. In **Settings → Capture quality**, set a minimum rate per sensor. If a sensor falls below it — before or during a recording — the operator must acknowledge a warning and can stop to fix it. They can still continue, because a live case may not be repeatable; it just becomes a deliberate choice.',
    more: 'The default floor is 50 % of each sensor\'s target (EEG 128 Hz, hand sensors 25 Hz). Set a floor to 0 to turn the warning off for that sensor. The amber "Low rate" badge on a recording is separate and always uses 70 %.',
  },
  {
    id: 'remembered-sensors',
    section: 'Recording a session',
    audience: STAFF,
    q: "Why doesn't the capture app ask me to pick the sensors every time?",
    a: 'It remembers the headband and hand sensors from the last session and reconnects to them automatically. It only asks again when one of them is not in range. To choose new sensors, use **Forget Paired Sensors** in the app\'s menu.',
  },
  {
    id: 'self-assessment',
    section: 'Recording a session',
    audience: TENANT_ALL,
    q: 'What is the short survey at the end of a recording?',
    a: 'Three optional questions about the case you just recorded — how much guidance you had, how you rate your own performance, and how complex the case was — one tap each. Your answers appear beside the recording as **Self-assessment**. Choosing **No thanks** is recorded too, so a skipped survey is not mistaken for a missing one.',
  },
  {
    id: 'audio-recorded',
    section: 'Recording a session',
    audience: TENANT_ALL,
    q: 'Does the capture app record audio?',
    a: "Yes, during the procedure (never during a baseline). The phone's microphone records the room so the transcript can be built and so camera video can be lined up with the sensors. Make sure your study's consent covers room audio.",
  },
  {
    id: 'baselines',
    section: 'Recording a session',
    audience: TENANT_ALL,
    q: 'What is a baseline?',
    a: 'A short calibration recording — eyes open, eyes closed and a little hand movement — that captures how someone looks at rest. The capture app asks for a new one when the participant changes or the last one is too old; nobody needs to schedule them.',
  },
  {
    id: 'capture-background',
    section: 'Recording a session',
    audience: TENANT_ALL,
    q: 'Can I switch to another app during a recording?',
    a: 'Yes. The recording carries on in the background while you answer a message or look something up, and the capture app is where you left it when you come back. Return to it to end the case, so you can stop the recording and answer the short survey.',
  },
  {
    id: 'auto-stop',
    section: 'Recording a session',
    audience: STAFF,
    q: 'Why did the capture app stop a recording after two minutes?',
    a: 'If no sensor sends any data in the first two minutes, the app stops the recording and says so, rather than recording nothing for as long as it is left running. Check that the headband and hand sensors are switched on and connected — **Test Sensor Rates** shows it — and start again.',
    more: 'A recording in which the sensors worked and then all went quiet stops with its own message. In the portal, a recording that captured nothing shows "Nothing was recorded — no sensor sent any data".',
  },

  // ── After a session ────────────────────────────────────────────────────
  {
    id: 'where-is-my-data',
    section: 'After a session',
    audience: TENANT_ALL,
    q: 'I just finished a session — where is its data?',
    a: 'Select the session: a row of steps under the **Cognitive trace** title shows **Sensor data**, **Video**, **Video prep**, **Scoring** and **Transcript**. Each is ✓ done, spinning, waiting, amber (needs a look) or red (failed); anything amber or red is written out underneath. In the session list, a dot marks every session still on its way in.',
    more: 'Phones and edge appliances report their uploads as they go, so a step can show a percentage ("Sensor data 41%"), name a camera that recorded nothing, or pass on the device\'s own reason for a failed upload. If a phone\'s upload failed, it retries when the capture app is opened again — so if **Sensor data** turns amber after a case, open the app on that phone.',
  },
  {
    id: 'awaiting-recovered',
    section: 'After a session',
    audience: TENANT_ALL,
    q: 'What do "Waiting for the phone to upload" and "Rebuilt from the live stream" mean?',
    a: 'They appear under **Sensor data**. **Waiting for the phone to upload**: the case was recorded but the phone has not sent the file yet — usually it is still off the network. If it says **Still not uploaded**, open the capture app on that phone so it retries. **Rebuilt from the live stream**: the phone\'s own upload never arrived, so the recording was rebuilt from what it streamed live; scores are there, but the transcript can be thin.',
    more: 'If the phone later sends its own copy, that complete copy replaces the rebuilt one and the case is re-scored. **Nothing was recorded — no sensor sent any data** means the phone never received anything from the sensors, so there is nothing to upload or score.',
  },
  {
    id: 'not-scored',
    section: 'After a session',
    audience: TENANT_ALL,
    q: 'A recording says "Not scored" — what does that mean?',
    a: 'Scoring was tried several times and has stopped; it will not finish on its own. Hover the badge for the reason. A recording of only a few seconds is simply too short to score and needs nothing. A missing file or a recording too large to score is worth telling us about — the recording is kept, so do not re-record it.',
    more: 'Tenant admins can list every one of them with the **Could not be scored** filter on Activities. A recording of several hours with every sensor running can be too large to score; it is kept, and we can score it for you.',
  },
  {
    id: 'computing-insights',
    section: 'After a session',
    audience: TENANT_ALL,
    q: 'What does "Computing insights…" mean?',
    a: 'A new recording is being scored. The page checks every few seconds and fills in the charts by itself when the scores arrive — usually within a couple of minutes. Use **Refresh** if you want to check straight away.',
  },
  {
    id: 'preparing-video',
    section: 'After a session',
    audience: STAFF,
    q: 'What is the "Preparing video & audio" indicator in the top bar?',
    a: 'After a video upload or a split, the video is aligned, trimmed and transcribed in the background. The indicator shows which session and how far along it is, and disappears when everything is ready. With several at once it shows "N preparing" — click it for the list.',
  },
  {
    id: 'ready-email',
    section: 'After a session',
    audience: PARTICIPANT,
    q: 'Will I be told when a session is ready to review?',
    a: 'Yes, by email, once per session — as long as your account has an email address and you are allowed to sign in. For your privacy it contains only the procedure name, the date and a sign-in link; your results stay in the portal.',
  },

  // ── Your results ───────────────────────────────────────────────────────
  {
    id: 'dashboard-sessions',
    section: 'Your results',
    audience: PARTICIPANT,
    q: 'What happens when I click a session on my Dashboard?',
    a: 'The **Cognitive trace** opens beside your session list — the video, the transcript and your focus line. The **Session metrics** scorecard appears underneath, with the **AI Coach** read beside it. Right-click a session (or long-press on a touch screen) for **Replay session** and **Attach video or audio**.',
  },
  {
    id: 'cognitive-trace',
    section: 'Your results',
    audience: TENANT_ALL,
    q: 'What does the Cognitive trace show?',
    a: 'How your EEG indices changed through the recording, second by second, measured by the headband. **Focus** and **Emotional engagement** show by default; click the chips under the chart to add **Workload**, **Stress**, **Fatigue** or **Valence**, or to hide any — your choice is remembered in this browser. Only the indices your organisation shows in **Settings → Metrics** appear. Above the chart are the recording\'s video and its transcript; beside it, when there is hand-motion data, the **3D hand path**.',
    more: 'Drag the 3D hand path to rotate it; click the chart and the path is drawn up to that moment. Focus = β/α, Emotional engagement = β/(α+θ), Workload = θ/α, Stress = β-high/α, Fatigue = (α+θ)/β, Valence = right minus left frontal alpha. Higher focus means more concentrated attention. An index the recording has no values for gets no chip, and a line under the chart says why: Stress and Fatigue exist only on sessions scored since mid-October 2026, and Valence needs both front electrodes. Values are raw ratios, so a series on a larger scale (Stress often is) can flatten the others; turn it off to compare the rest. One screen width shows 10 minutes; a longer recording scrolls sideways. The **Show** buttons above the chart change the span — 2 min, 10 min, 1 hr or Whole.',
  },
  {
    id: 'moment-video',
    section: 'Your results',
    audience: TENANT_ALL,
    q: 'How do I watch the video and hear what was said at a moment?',
    a: 'Click anywhere on the trace: a marker lands there, the video jumps to that moment and plays, and the transcript scrolls to the nearest line. Click a transcript line to do the same from the other side. Click the same moment again to stop. **Play all** beside the Cognitive trace title replays the whole session: every camera and the sound play together while the marker moves along the trace. It starts at the marker (or the beginning) and **Pause** stops it where it is; press **Play all** again to carry on.',
    more: 'A recording with several cameras shows all angles side by side. Video from a camera without its own sound plays with the phone\'s room audio. To make one camera bigger, hover over it and drag the corner handle at its top right: right or up makes it bigger, left or down smaller. Double-click the handle to put it back. With the handle selected, the arrow keys resize it too. A camera that starts later in the session joins the replay when the marker reaches it. Pausing or scrubbing one video during **Play all** pauses or moves the whole replay.',
  },
  {
    id: 'transcript-source',
    section: 'Your results',
    audience: TENANT_ALL,
    q: 'Where does the transcript come from?',
    a: "From the room audio the phone records during the case. It is turned into English text and split into timestamped lines of what was said. Transcripts are only made when your organisation's 10NEWTONS administrator has switched the feature on, and they arrive a few minutes after the scores.",
    more: 'When a room is silent, speech-to-text tends to invent words — often phrases like "Thank you for watching". Those lines are hidden, and the panel tells you how many, so a quiet case reads as quiet rather than broken. Very long cases are transcribed in pieces and joined back together. Transcripts are always written in English, so a noisy or quiet stretch is not mistaken for another language. If the phone\'s microphone recorded nothing at all (muted, or in use by another app), no transcript is made and the session\'s **Transcript** step says **No sound was recorded**; the camera\'s sound is used instead when it has any. The transcript does not depend on the headband: a recording whose EEG was unusable is transcribed in 30-second lines.',
  },
  {
    id: 'transcript-patient-info',
    section: 'Your results',
    audience: TENANT_ALL,
    q: 'Does the transcript contain patient information?',
    a: "It is removed. The surgical time-out, where the patient's name, record number and date of birth are read aloud, is replaced by a \"Patient identification\" marker with no text, and identifiers spoken elsewhere are blanked out. Clinical numbers — doses, counts, blood pressures — are left in because they matter for coaching.",
    more: 'The audio is sent to a speech-to-text service before this clean-up runs, so the clean-up protects what we store, show and export, not what is sent for transcription.',
  },
  {
    id: 'scorecard',
    section: 'Your results',
    audience: TENANT_ALL,
    q: 'How do I read the Session metrics scorecard?',
    a: 'Each gauge reads worse → better from left to right, with marks for the reference groups (Novice, Practice-ready, Expert). Your session is the orange bar with a black dot. Once you have five or more sessions of a procedure, a shaded box shows your usual range, so you can see whether today was typical, a best or an off day. Double-click a gauge to enlarge it.',
  },
  {
    id: 'faded-references',
    section: 'Your results',
    audience: TENANT_ALL,
    q: 'Why are some reference marks faded, and why do some metrics have no verdict?',
    a: 'A solid mark is a reference the research found to genuinely separate skill levels, so we grade you against it. A faded, dashed mark was not statistically significant: it is shown for context but never used for a verdict. A metric whose references are all faded therefore shows no verdict — we would rather say nothing than overclaim.',
  },
  {
    id: 'ai-coach',
    section: 'Your results',
    audience: TENANT_ALL,
    q: 'What does the AI Coach card say, and where does it come from?',
    a: 'A short plain-language read of the session: the headline, a few points and a next goal, plus how the session moved against your recent ones and which metrics are already at expert level. It is based on the session\'s measured metrics and the reference groups. When the written read is not available, the card works out the biggest gap and a concrete goal from the metrics instead.',
  },
  {
    id: 'ai-coach-later',
    section: 'Your results',
    audience: TENANT_ALL,
    q: 'My scores are in but the AI Coach card is still empty — why?',
    a: 'The written read is prepared after the scores, usually within a few minutes, and the card fills in by itself. On a busy day it can take longer. If the AI service is unavailable, transcripts and AI Coach reads wait and then carry on by themselves — nothing needs to be re-recorded.',
  },
  {
    id: 'ai-coach-debrief',
    section: 'Your results',
    audience: TENANT_ALL,
    q: 'What is "From your debrief" on the AI Coach card?',
    a: 'When a coaching debrief has been attached to the session as **Coaching video** or **Coaching audio**, the AI Coach picks out the coach\'s main points. Each point shows the coach\'s own words and the time in the debrief, so you can listen to it under **Transcript**. It also says whether the session\'s metrics back the point up, point the other way, or say nothing about it.',
    more: 'An admin turns this on in **Settings → Compare → Let the AI Coach use coaching debriefs**; it is off until then. Only the written debrief transcript is used, with patient details already removed. No video, audio or participant names are sent. A quote is only shown if those exact words are in the transcript. Points appear once the debrief has been transcribed, or the next time the session is scored.',
  },
  {
    id: 'path-length',
    section: 'Your results',
    audience: TENANT_ALL,
    q: 'What are the hand-motion metrics?',
    a: '**Angular path length** — how far each hand turned — is the most reliable economy-of-motion measure. **Motion smoothness** (steadier is better), **tremor power** (lower is steadier), **bimanual coactivity** (how the hands work together) and **active duration** / **idle time** describe how the hands moved.',
    more: 'Hand **path length**, **hand speed** and the older **bimanual dexterity** are hidden by default and never marked better or worse. A bench test against measured distances showed the wrist sensor cannot measure how far or how fast the hand moved: slow movements go missing and longer ones are exaggerated. There are also no validated reference values for them. An admin can still show them in **Settings → Metrics → Scorecard metrics** as rough relative figures, and they stay in the data export for research. The 3D hand path in the replay is illustrative for the same reason: it shows the pattern and direction of movement, not exact distances.',
  },
  {
    id: 'performance-grid',
    section: 'Your results',
    audience: PARTICIPANT,
    q: 'What is the "Your performance" section?',
    a: 'Your averages per procedure across all your sessions — duration, focus, emotional engagement and more — next to reference values from experienced surgeons where your procedure has them. Pick a procedure from its dropdown to focus on one. The Session metrics scorecard is the single-session version.',
  },
  {
    id: 'quality-badge',
    section: 'Your results',
    audience: TENANT_ALL,
    q: 'What does the OK / Low rate / Bad badge on a session mean?',
    a: 'How well the sensors recorded. **OK**: every sensor kept its target rate. **Low rate**: at least one ran below about 70 % of target, often because of radio interference. **Bad**: a connected sensor produced nothing usable. A sensor that was not worn reads "not used" rather than Bad. Hover the session for the per-sensor detail.',
  },
  {
    id: 'new-version',
    section: 'Recordings & activity',
    audience: STAFF,
    q: 'What does "A new version of 10 Newtons is available" mean?',
    a: 'The portal was updated while your tab was open. Your tab keeps running the version it loaded until you reload it, and some things may not work as they should until then. Choose **Reload** when you are ready; **Later** hides the message for now.',
  },
  {
    id: 'replay',
    section: 'Your results',
    audience: TENANT_ALL,
    q: 'How do I replay a recording?',
    a: 'Choose **Replay session** from the session\'s menu (right-click it). You see the same live view the capture app showed — EEG bands, hand motion, trait bars and a 3D hand path — with play, pause, a scrubber and 1×/2×/4× speed. The 3D path shows relative movement, not exact position.',
    more: 'Replay reads the stored recording in your browser, piece by piece, and shows how much it has read; a two-hour recording opens in a few seconds. The 3D hand path is rebuilt for up to about half an hour of hand motion; for a longer recording the replay says so and shows everything else.',
  },
  {
    id: 'trait-bars',
    section: 'Your results',
    audience: TENANT_ALL,
    q: 'How do I read the trait bars (Focus, Stress, Fatigue…)?',
    a: 'Each bar shows **how much of that trait** there is in the current second, compared with a reference group of surgeons recorded at a 2026 surgical congress: **50 % is typical**, 90 % is higher than nine in ten. A high **Fatigue**, **Stress** or **Tremor** bar means more of it; a high **Focus**, **Steady** or **Smoothness** bar means more of that.',
    more: 'A bar shows **—** when there is nothing reliable to score — for example when every headband electrode is off the skin. Electrodes that are not touching are left out instead of being counted as brain activity. The bars move second to second; look at the trend, not a single reading.',
  },
  {
    id: 'export-my-data',
    section: 'Your results',
    audience: PARTICIPANT,
    q: 'Can I download my own data?',
    a: 'Yes. **Export my data** on your Dashboard builds a zip of your sessions and their scores across every procedure, as **CSV** or **JSON**, and saves it to your Downloads. It contains your scores, not the raw sensor recording.',
    more: 'If you enrolled at a conference booth, it also has the consent you signed (with the form\'s text and your signature) and your survey answers, plus your ratings of your recordings.',
  },
  {
    id: 'one-org-at-a-time',
    section: 'Your results',
    audience: PARTICIPANT,
    q: 'I take part at more than one organisation — can I see one at a time?',
    a: 'Yes. A **This tenant / All tenants** toggle appears on your Dashboard when you belong to more than one organisation. "This tenant" follows the organisation chosen in the top bar.',
  },

  // ── Analysis & reports ─────────────────────────────────────────────────
  {
    id: 'analysis',
    section: 'Analysis & reports',
    audience: ['tenant_admin', 'coordinator', 'participant', 'superadmin'],
    q: 'What is Analysis?',
    a: "Analysis compares a whole team on one case. Pick a **case** — recordings made together — and the **By case** tab lays everyone's focus, engagement and hand motion on one shared clock. Numbered **Diverged at** marks show where the team pulled apart most; click one to jump the video and transcript there.",
    more: "The top panel shows everyone's video side by side and, for the moment you pick, each person's value and the spread between them. Only the focused person's clip plays sound. Click a point on a trace to seek that person's video and reveal their 3D hand path up to that moment.",
  },
  {
    id: 'build-case',
    section: 'Analysis & reports',
    audience: TENANT_ALL,
    q: 'How do I create a case?',
    a: '**Analysis → New case**: filter by procedure, people and date, tick the recordings that belong together, and the case names itself. Staff can use any recording in the tenant and can edit or delete cases. Participants can create a case from their own recordings plus anyone who was recording at the same time, and it must include at least one of their own.',
  },
  {
    id: 'analysis-participant',
    section: 'Analysis & reports',
    audience: PARTICIPANT,
    q: 'What can I see in Analysis as a participant?',
    a: 'Every case you took part in, with the whole team — traces, video and hand paths. The **Benchmark** tab shows your own metrics against the reference groups and, if your organisation has turned it on, against an anonymous group average (the whole cohort or an experience band). You never see another named participant\'s numbers there.',
  },
  {
    id: 'benchmark-staff',
    section: 'Analysis & reports',
    audience: STAFF,
    q: 'How do I compare participants or groups?',
    a: "Use **Analysis → Benchmark**. Pick a procedure and two sides — a participant, an experience band (≤ 2, 3–7, 8+ years) or a hand-picked group — and each metric shows both sides on one worse → better gauge, with the reference marks for scale and the better side highlighted.",
    more: "A group's value is the average over all its members' sessions. Tenant admins can turn Benchmark on or off, and offer participants their own anonymous comparison, in **Settings → Compare**.",
  },
  {
    id: 'cohort-report',
    section: 'Analysis & reports',
    audience: STAFF,
    q: 'How do I put everyone who did a procedure side by side?',
    a: "Use **Analysis → Reports**. Pick one or more procedures, optionally a date range (an event) and a role, and click **Build report**. You get a coverage table, how long each person took, and for each procedure one row per participant by name, with each value coloured by where it sits among the people who did that procedure. **Print / save as PDF** turns it into a document.",
    more: "With two or more procedures, **Across stations** shows each person's percentile at every station they did, and the EEG section shows which station stood out for each person. Only metrics shown on the scorecard for each procedure are included, following **Settings → Metrics** and any per-procedure setting. Participants don't see this tab.",
  },
  {
    id: 'cohort-report-colours',
    section: 'Analysis & reports',
    audience: STAFF,
    q: "Why aren't duration and EEG coloured better or worse in a report?",
    a: "Faster isn't obviously better in a clinical scenario, so duration is shaded light to dark without a verdict. EEG levels differ more between people, and with how the headband sits, than between stations, so ranking people on them would mostly compare individuals. Instead the report compares each station with the person's own average over the stations they did.",
    more: "Smoothness is coloured, but ranked with the effect of case length removed: the measure scores longer cases as rougher whatever the hands did. The number shown is still the scorecard's value. Sessions with nothing recorded, under 30 seconds, or without wrist-sensor activity (for hand metrics) are left out and counted in the coverage table.",
  },
  {
    id: 'case-picker',
    section: 'Analysis & reports',
    audience: TENANT_ALL,
    q: 'Several cases have the same name — how do I tell them apart?',
    a: 'Each case in the picker shows its date and time and, underneath, who is in it and how many recordings it holds.',
  },
  {
    id: 'pdf-report',
    section: 'Analysis & reports',
    audience: TENANT_ALL,
    q: 'Can I download a session as a PDF report?',
    a: "Yes. Open the session's scorecard and click **Download report**. The PDF opens with the cognitive trace, then the AI Coach read, then every metric against its reference groups. It saves straight to your Downloads, and a line beside the button confirms the file name. Participants can download reports for their own sessions.",
    more: 'The first download of a session takes a few seconds while the PDF is built; after that it downloads instantly. A report made before a session was re-scored keeps its original content.',
  },
  {
    id: 'longitudinal',
    section: 'Analysis & reports',
    audience: TENANT_ALL,
    q: 'How do I see progress over time?',
    a: "On a session's scorecard, the procedure name is a menu: choose **Longitudinal reports** to see every metric across someone's sessions of that procedure: each session as a dot, with a trend line fitted through all of them. A metric reads Improving or Declining only when the trend is statistically clear; otherwise it says No clear trend. **Download report** saves it as a PDF.",
    more: 'The trend line is a least-squares fit against session number, with a shaded 95% confidence band. Under each chart: the slope (change per session) with its 95% confidence interval, R² (how much of the session-to-session variation the line explains), the p-value and the number of sessions. A trend counts when p < 0.05 and the line moves at least 5%; it needs at least 3 sessions. Staff can add other participants with **Compare with** to overlay their progress, lined up by session number.',
  },
  {
    id: 'smoothing',
    section: 'Analysis & reports',
    audience: ADMIN,
    q: 'Can I smooth a noisy trace?',
    a: 'Yes — the slider on the Cognitive trace card sets a smoothing interval from Off to 30 seconds. It affects everyone viewing that session and can be changed back at any time; the original detail is kept.',
  },
  {
    id: 'time-zone',
    section: 'Analysis & reports',
    audience: ['tenant_admin', 'superadmin'],
    q: 'Which time zone are times shown in?',
    a: "By default each viewer's own. In **Settings → Tenant** you can switch your organisation to UTC so everyone sees the same clock — useful across sites. It only changes how times are displayed; nothing stored changes.",
  },

  // ── Live ───────────────────────────────────────────────────────────────
  {
    id: 'live-participant',
    section: 'Live',
    audience: PARTICIPANT,
    q: 'Can I watch my case live while it is being recorded?',
    a: 'Yes. **Live** in the sidebar shows the case you are recording in right now, with your whole team overlaid and a marker the moment your signals diverge. You appear as **You**; click it for your own full live view. Only cases you are part of show up, and the page is view-only.',
  },
  {
    id: 'live-staff',
    section: 'Live',
    audience: STAFF,
    q: 'How do I watch recordings live?',
    a: "Open **Live** in the sidebar. Every case being recorded appears within seconds, with its participants overlaid on shared axes and a marker when their signals diverge. Click a person's name for their full live view. A case disappears when the recording ends.",
    more: 'Live is a preview streamed from the phone, so a weak network gives a patchy picture; the complete recording still uploads at the end.',
  },
  {
    id: 'live-split',
    section: 'Live',
    audience: STAFF,
    q: 'Two unrelated sessions were overlaid into one live case — how do I separate them?',
    a: "Click the split icon on the person's name under the case heading. They move onto their own track, for everyone watching, until you click **Merge back**. Live groups sessions by procedure, so a practice session running beside a real case can land on the same wall; splitting changes only the live view, never the recordings.",
  },

  // ── Recordings & activity ──────────────────────────────────────────────
  {
    id: 'find-recording',
    section: 'Recordings & activity',
    audience: TENANT_ALL,
    q: 'How do I find one recording among hundreds?',
    a: 'Every list of recordings has the same filter bar: type to search participants, procedures and devices, pick several at once, and choose a date preset or **Exact range…** down to the time. The filter is part of the page address, so Back undoes it and you can share the link.',
    more: '**Activities** says how many recordings match and shows the most recent first; **Load more** brings in older ones. Dashboard lists show the 200 most recent matches, but the filter searches your whole history first, so older recordings are still found.',
  },
  {
    id: 'activity-views',
    section: 'Recordings & activity',
    audience: STAFF,
    q: 'Can I group Activities by participant or device?',
    a: 'Yes — the toggle at the top of **Activities** switches between a day-by-day feed, **By participant** and **By device**. Each session also names the device (or participant) it belongs to, so nothing is lost when a card is collapsed.',
  },
  {
    id: 'download-recording',
    section: 'Recordings & activity',
    audience: STAFF,
    q: 'How do I download a recording?',
    a: "Right-click the session on **Activities** and choose **Download session bundle** — a zip with the sensor recording and any video and audio. Baselines offer **Download baseline**. Tenant admins and coordinators can download; read-only admins cannot.",
    more: "If the participant enrolled at a conference booth, the zip also has a **participant** folder: the consent they signed (the exact form version, their choices, signed name and time, and the signature image) and their survey answers, as a file and as a spreadsheet. If they rated the recording afterwards, or declined to, that is in **session-rating.json**. Badge photos are not included.",
  },
  {
    id: 'export-all',
    section: 'Recordings & activity',
    audience: [...STAFF, 'superadmin'],
    q: "Can I download all my organisation's data?",
    a: "Yes. **Download recordings** on the Participants page gives one file per participant, with a folder for each session holding what a single session download holds. Its first row, **Everyone, as tables**, saves all participants' sessions, scores, consents and survey answers in one small CSV or JSON file.",
    more: "Sessions without a participant are listed as **Unassigned recordings**. The recordings come one participant at a time because with video an organisation's recordings run to tens of gigabytes. Each file streams straight to your Downloads and can take a while; a file that could not be read from storage is left out and listed in **MISSING_FILES.txt** inside the download. The tables file is built fresh every time, so it always has the latest sessions. It also holds the consent and survey data of people who enrolled but never recorded: every signed consent (choices, signed name and time) with its signature image, every survey answer, every rating of a recording (also declined ones), and the full text of every version of your consent forms and surveys in **forms.json**, so each signature can be matched to the exact wording. Badge photos are not included.",
  },
  {
    id: 'disable-recording',
    section: 'Recordings & activity',
    audience: STAFF,
    q: 'How do I remove a bad recording or baseline?',
    a: "Right-click it on **Activities** and choose **Disable**. It disappears from lists, results and comparisons, but nothing is deleted. To bring it back, turn on **Show disabled** and choose **Restore**.",
  },
  {
    id: 'split-recording',
    section: 'Recordings & activity',
    audience: STAFF,
    q: 'How do I split one recording into several sessions?',
    a: 'Right-click the session on **Activities** and choose **Split into sessions**. Scrub the video or the trace to each boundary and click **Add split at…**, keep or drop each segment, and optionally name each with a procedure. Each kept segment becomes its own session, scored separately; the original is disabled, not deleted.',
    more: 'The split itself runs in the background, so a long recording doesn\'t keep you waiting: you can close the dialog, the top bar shows "Splitting the recording" until it is done, and the new sessions then appear. Any video or audio is trimmed to match each new session afterwards — the "Preparing video & audio" indicator shows progress. If a boundary was wrong, restore the original and disable the new sessions.',
  },
  {
    id: 'tag-recording',
    section: 'Recordings & activity',
    audience: STAFF,
    q: 'How do I tag a recording with its department, facility or case number?',
    a: "Right-click the session on **Activities** and choose **Set department / location**. Pick the department and facility, the kind of event, and an optional case reference number (never patient details). Setting a default department and facility on a device tags its recordings automatically.",
  },
  {
    id: 'procedure-instances',
    section: 'Recordings & activity',
    audience: STAFF,
    q: 'What is a procedure instance?',
    a: 'One real event performed by a team — for example three clinicians each recording the same operation. On **Procedures → Instances**, create one, add the team with their roles, and attach the recordings that belong to it. Detaching a recording or deleting an instance never deletes a recording.',
  },
  {
    id: 'attach-video',
    section: 'Recordings & activity',
    audience: TENANT_ALL,
    q: 'Can I add a video or audio file to a recording?',
    a: "Yes — right-click the session and choose **Attach video or audio**. The file is lined up with the recording automatically using its sound, then appears on the trace. You can attach a video and its separate audio together.",
  },
  {
    id: 'record-debrief',
    section: 'Recordings & activity',
    audience: STAFF,
    q: 'How do I record a debrief?',
    a: 'Open the participant’s session and choose **Record debrief** next to **Transcript**. The cameras of the edge appliance (the ones that recorded the session are chosen) record the picture, and this laptop records the sound. Keep reviewing the session while it records; stop it from the bar at the bottom of the screen. It is then added to the session as a coaching recording and transcribed, like an uploaded debrief.',
    more: 'The laptop’s microphone hears everything in the room, including the recording if you play it with sound — use headphones or keep it muted while you talk. A debrief stops by itself after an hour, and if the laptop is closed the cameras stop within a few minutes. With more than one camera, the debrief has a **Camera** switch; the transcript is the same for all.',
  },
  {
    id: 'video-clock',
    section: 'Recordings & activity',
    audience: TENANT_ALL,
    q: 'What is the clock in the corner of the video?',
    a: 'It is the time of day the frame you are watching was recorded. The player’s own counter shows how far into the clip you are; this clock shows when it happened, so you can line the video up with notes, other cameras or anything else timed by the clock on the wall. It runs while the video plays and follows it when you scrub.',
    more: 'It is shown in the portal’s display time zone, like the other times on the page, and it comes from the frame itself — so it stays right when a camera started after the session or stopped before it.',
  },
  {
    id: 'coaching-sessions',
    section: 'Recordings & activity',
    audience: TENANT_ALL,
    q: 'Can I add a coaching session recorded after the procedure?',
    a: "Yes — in **Attach video or audio**, set the file's type to **Coaching video** (or **Coaching audio** for a sound-only file) instead of **Session video / audio**. It keeps its own timeline rather than being lined up with the procedure, and its speech is transcribed in full, which takes a few minutes.",
    more: "On the session, a **Transcript** switch appears above the video: **Procedure recording** or the coaching session. Choosing the coaching session shows its own player and transcript; click a line to play from there. If the transcript fails, **Try again** starts it over. Patient identifiers are removed from the transcript, as they are from the procedure's. If your admin has turned it on, the AI Coach card then shows the coach's main points under **From your debrief**.",
  },

  // ── Participants ───────────────────────────────────────────────────────
  {
    id: 'invite-participants',
    section: 'Participants',
    audience: STAFF,
    q: 'How do I invite participants?',
    a: 'On **Participants**, invite one person by email, or use **Bulk invite** to paste a list or upload a file (commas, semicolons or new lines). The results show who was invited, who was already a member and which addresses were invalid.',
    more: 'You can invite someone who already takes part at another organisation; you will see a note naming it, and nothing stops the invitation.',
  },
  {
    id: 'pending-invites',
    section: 'Participants',
    audience: STAFF,
    q: "I invited someone but they haven't joined — can I resend?",
    a: "Invitations not yet accepted are listed under the roster in **Pending invitations**, with their expiry dates. The envelope icon sends a fresh 7-day link (the old one stops working); the × cancels the invitation.",
  },
  {
    id: 'direct-entry',
    section: 'Participants',
    audience: STAFF,
    q: 'Can I add participants without email invitations?',
    a: 'Yes, in direct-entry mode (turned on in **Settings → Tenant**). **Add participant** creates someone straight away from a name, with optional job title, experience and email — useful for demos. US clinicians can be filled in from their NPI with **Look up**. Tick **Email them a set-password link** if they should be able to sign in.',
    more: '**Bulk add** does the same for a whole list: paste or upload a sheet with one person per row — a name is all that\'s needed; email, job title and years of experience are optional columns. People without an email are recorded on devices by an operator. The results show who was added, who was already there and which rows were skipped.',
  },
  {
    id: 'edit-participant',
    section: 'Participants',
    audience: STAFF,
    q: "Can I edit a participant's details?",
    a: "Yes — open the ⋮ menu on their row and choose **Edit** for name, job title, years of experience and email. Changing the email changes how they sign in, so it must not belong to another account; tick **Email them a set-password link** to send a sign-in link to the new address. The **Allow sign-in** switch in the same dialog decides whether they can sign in at all.",
  },
  {
    id: 'suspend-participant',
    section: 'Participants',
    audience: STAFF,
    q: 'How do I suspend or remove a participant?',
    a: "Open the ⋮ menu on their row. **Disable access** suspends someone: they keep their history but lose access until you choose **Re-enable access**. **Remove from tenant** removes them from your organisation; their account and recordings remain, and you can invite them again later.",
  },
  {
    id: 'conference-intake',
    section: 'Participants',
    audience: ADMIN,
    q: 'How do I enroll people at a conference booth?',
    a: 'Set up **Settings → Conference intake**: use **Add form** to start from the ready-made consent form and survey templates (or a blank form of your own), choose the consent form and survey, switch on **Enroll participants with conference intake** and save. At the booth, **Enroll at booth** shows a QR code visitors scan to enroll on their phone (next question); **Fill in on this laptop instead** is the tablet flow: badge photo, details, then hand over for consent and the survey.',
    more: 'Each edit to a form saves a new version; answers already collected keep the version the person saw, and each participant\'s record shows the exact consent text they signed. The download icon next to a survey saves all its responses as a CSV file. Signing consent on a tablet is electronic consent: make sure your IRB has approved the electronic format, and that the form text matches the version they approved.',
  },
  {
    id: 'copy-intake-form',
    section: 'Participants',
    audience: ADMIN,
    q: 'Can I reuse a consent form or survey from another conference?',
    a: 'Yes. In **Settings → Conference intake**, **Add form → Copy from another organisation…** lists the forms of the other organisations you run; pick one and choose **Copy**. To start a variant of a form you already have here, use the copy icon on its row: the copy opens in the editor straight away.',
    more: 'A copy is a new form starting at version 1, with the latest version of the original. Changing it doesn\'t change the original, and no responses come with it. A copy is marked **Copy** and can be deleted with the bin icon, as long as intake isn\'t using it and nobody has signed or answered it yet; after that, archive it. Original forms can only be archived, never deleted. Choose it under **Consent form** or **Survey** and save to use it at the booth. A platform admin working in an organisation through a support session can copy from any organisation.',
  },
  {
    id: 'post-surveys',
    section: 'Participants',
    audience: ADMIN,
    q: 'How do I set up a survey after a recording or a simulation?',
    a: 'In **Settings → Conference intake**, use **Add form → Post survey**: a new one for the capture app (**OR**) or for the simulation station (**Sim**), or a copy of the capture app’s current three questions. Then choose it under **Post surveys** and save. The OR one is shown in the capture app after each recording; the Sim one is answered on the participant’s own phone from the QR code that **Participants → Post survey** shows at the simulation station (next to **Enroll at booth**; **Print** makes a poster).',
    more: 'Forms are listed as **Consent**, **Pre survey** (the booth survey) and **Post survey · OR / Sim**. Without an OR post survey the capture app asks its built-in three questions, as before. An OR post survey offers only the question types the app can show. A post survey can be switched between OR and Sim in the editor, unless it is the one in use. Answers are in **Download responses**, in **Export all data** (post_survey_answers.csv) and in each recording’s download (post-surveys.json).',
  },
  {
    id: 'sim-post-survey-phone',
    section: 'Participants',
    audience: STAFF,
    q: 'How does a participant answer the sim post survey?',
    a: 'At the simulation station, open **Participants → Post survey** (shown when conference intake is on and a post survey (Sim) is chosen). After the simulation participants scan its QR code with their phone camera. If they enrolled on that phone at the booth, it already knows who they are; otherwise they type the email or badge code they enrolled with. They pick which of their recordings from the last two weeks the answers are about, and answer. Answering the same recording again replaces the earlier answers.',
  },
  {
    id: 'booth-self-enroll',
    section: 'Participants',
    audience: STAFF,
    q: 'How do visitors enroll on their own phone at the booth?',
    a: 'Click **Enroll at booth** on **Participants** and leave the laptop showing the QR code. A visitor scans it with their phone camera, photographs their badge (or types their details, NPI optional), signs the consent and answers the survey on the phone. Each code works for one phone only: as soon as someone opens it, the laptop shows a new one, so several people can enroll at the same time. The laptop counts how many are filling in now and how many enrolled today.',
    more: 'They are added straight away, marked **Self-enrolled**, with **Sign-in off** (next question). If their email or name matches someone already in your organisation, they are also marked **Possible duplicate** so you can check; an email that belongs to another account is shown there. If they gave an email, they get one thank-you after their first recording is processed, with no results in it. A link opened but not finished expires after 30 minutes; an unused code is replaced every few minutes. To enroll at the booth at all, a tenant admin turns on conference intake in **Settings**.',
  },
  {
    id: 'participant-status',
    section: 'Participants',
    audience: STAFF,
    q: 'What do the status labels in the participant list mean?',
    a: '**Signed in**: they have signed in at least once. **Invited**: they have an email and a sign-in link but have not used it yet. **Sign-in off**: their **Allow sign-in** switch is off — they can\'t sign in or reset a password, and no invitation or results email goes out. **No login**: no email on file, so an operator records them. **Disabled**: their access is suspended.',
    more: 'Everyone who enrolls on their own phone at the booth starts with **Sign-in off**. To invite them, choose **Edit** and turn on **Allow sign-in**: that emails them a link to set a password. Turning it off again signs them out.',
  },
  {
    id: 'participant-menu',
    section: 'Participants',
    audience: STAFF,
    q: 'How do I resend a sign-in link, or see what someone filled in at the booth?',
    a: 'Both are in the ⋮ menu on their row. **Resend portal link** emails a fresh link to set a password; it is greyed out while sign-in is off. **View intake** shows what they filled in at the booth — their details, the consent they signed and their survey answers.',
  },
  {
    id: 'license-limit',
    section: 'Participants',
    audience: STAFF,
    q: 'Why was a participant or device refused because of the license?',
    a: "Your license allows a number of participants and devices. A little over the limit is still allowed; beyond that new ones are refused with a message saying so, and bulk adds and invitations mark those rows **License limit**. Disable participants who have finished or retire devices that are out of use, or ask your 10NEWTONS representative to raise the limit.",
  },

  // ── Devices ────────────────────────────────────────────────────────────
  {
    id: 'enroll-device',
    section: 'Devices',
    audience: STAFF,
    q: 'How do I set up a capture phone?',
    a: "On **Devices**, click **Enroll device** and **Generate code**. In the capture app's enrollment screen, name the device and scan the QR code (or type the 8-character code). The code works once and expires after 10 minutes.",
    more: 'Device names must be unique among your devices, ignoring capitals and spaces. Names of retired devices can be reused.',
  },
  {
    id: 'install-capture-app',
    section: 'Devices',
    audience: STAFF,
    q: 'How do I install the capture app on a new phone?',
    a: 'On **Devices**, click **Install capture app** and follow the steps on the phone. If Google Play Protect warns about the app, tap **More details**, then **Install anyway**. Then enroll the phone with a code from **Enroll device**.',
    more: "The app's data is not copied to a new phone or restored from a backup, so a replacement phone is enrolled as a new device; retire the old one.",
  },
  {
    id: 'device-defaults',
    section: 'Devices',
    audience: ADMIN,
    q: 'Can a device tag its recordings with a department and facility?',
    a: "Yes. Edit the device on **Devices** and pick a default department and facility — the room it lives in. Every new recording from it is tagged automatically, and you can still change any single recording's tags.",
  },
  {
    id: 'sensor-health',
    section: 'Devices',
    audience: STAFF,
    q: "How do I see whether a device's sensors are getting worse?",
    a: "Click the **Sensor health** icon on the device's row. It charts each sensor's sampling rate across the device's recent sessions, lists the rate for every session, and flags a sensor that has run low repeatedly — your cue to charge or replace it, or rethink the room setup.",
  },
  {
    id: 'device-status',
    section: 'Devices',
    audience: STAFF,
    q: 'What is the difference between Suspend and Retire?',
    a: '**Suspend** stops a device uploading while it is out of service; **Re-activate** undoes it. A device shown as **Lost** is stopped the same way; **Found — mark active again** brings it back. **Retire** takes a device out of service for good: it is hidden (turn on **Show retired** to see it) and its past recordings stay intact. Retiring is for tenant admins.',
  },
  {
    id: 'device-share',
    section: 'Devices',
    audience: ADMIN,
    q: 'Can one tablet record for several of our organisations?',
    a: "Yes, if you administer both. Click the **Share** icon on the device's row and switch on each organisation it should serve — no second enrollment needed. The operator then picks the organisation in the capture app before each case. Switching one off stops the tablet reaching that organisation; its past recordings there stay.",
  },
  {
    id: 're-registered',
    section: 'Devices',
    audience: STAFF,
    q: 'A phone was re-registered — what about its old entry?',
    a: 'Re-registering creates a new device entry and disconnects the old one, which cannot upload; retire it to keep the list tidy. Its past recordings are unaffected.',
  },
  {
    id: 'app-version',
    section: 'Devices',
    audience: STAFF,
    q: 'A device shows an old app version — does it update?',
    a: 'Yes, the next time the app is opened after the update. If it still shows the old version, open the app on that phone, wait a moment, and click **Refresh** on Devices.',
  },
  {
    id: 'app-update-required',
    section: 'Devices',
    audience: STAFF,
    q: "The capture app says **Update required** and won't start a recording — what do I do?",
    a: 'This version of the app isn\'t supported. Tap **Download** on the red banner on the start screen, install the update, and open the app again; recording works as soon as the new version runs. A recording that is already running is never stopped.',
    more: 'A blue **Update available** banner is only a notice: you can keep recording and install the update after the session. Android may warn that the app comes from outside the Play Store; tap **More details**, then **Install anyway**.',
  },
  {
    id: 'app-sync',
    section: 'Devices',
    audience: STAFF,
    q: 'Do I need to tap Sync now after adding a participant or procedure?',
    a: "No. The capture app refreshes participants, procedures and settings by itself whenever it is on its start screen: when it opens, after each session, and when you come back to it. Use **Settings → Sync now** only if you can't wait a minute, or to see an error if something won't arrive.",
    more: 'Operators can also add or edit procedures on the device itself; changes appear in the portal straight away.',
  },
  {
    id: 'device-id',
    section: 'Devices',
    audience: STAFF,
    q: 'What is the Device ID column?',
    a: "The phone's own identifier, so the same physical phone can be recognised even when it is enrolled in more than one organisation. It does not identify a person.",
  },

  // ── Cameras & edge appliances ──────────────────────────────────────────
  {
    id: 'what-is-appliance',
    section: 'Cameras & edge appliances',
    audience: STAFF,
    q: 'What is an edge appliance, and do I need one?',
    a: 'A small computer in the room (Windows, Linux or Mac) that records the room cameras in step with the capture app. You only need one for video — the capture app records the sensors on its own. One appliance serves one room; add a camera for each angle.',
  },
  {
    id: 'install-appliance',
    section: 'Cameras & edge appliances',
    audience: ADMIN,
    q: 'How do I install and connect an edge appliance?',
    a: 'Open **Cameras → Add appliance** and download the build for the computer\'s system. The download already contains your server and a one-time code: unzip it and double-click **start.command** (Mac) or **start.bat** (Windows). It connects and shows as **Online** here within moments.',
    more: 'The code in a download expires after about 10 minutes; if it has, add the appliance again for a fresh one. On a Mac, the first start may need right-click → Open. Keep the computer\'s clock set automatically — video alignment depends on it. When updating an appliance, keep its settings files so it is not registered as a new one.',
  },
  {
    id: 'appliance-updates',
    section: 'Cameras & edge appliances',
    audience: ADMIN,
    q: 'How does an edge appliance get a new version?',
    a: "It updates itself. At startup and every few hours it checks for a newer release, shows a banner in its window, and installs it while nothing is recording or uploading, then restarts. On **Cameras**, an **Update** chip next to a box's version shows it is behind; hover it to see what will happen.",
    more: "A very old appliance can't update itself: download the new build once and replace the folder, keeping the settings files. A release that needs new camera tools is marked as a manual update. If you would rather an appliance only told you about updates, contact us. If a box starts with a video it never uploaded, it uploads it first and shows the progress in its window — leave it open until it finishes.",
  },
  {
    id: 'add-cameras',
    section: 'Cameras & edge appliances',
    audience: ADMIN,
    q: 'How do I add cameras to an appliance?',
    a: "Open the appliance and add each camera — a USB or built-in webcam, or a network camera by its stream address. For USB cameras, choose a resolution the camera can keep up (a 4K webcam is often smoothest at a lower size). The capture-app operator then picks the appliance and cameras for each session.",
  },
  {
    id: 'live-check',
    section: 'Cameras & edge appliances',
    audience: STAFF,
    q: 'How do I check that a camera is pointed correctly?',
    a: 'Use **Live check** on a camera, or **Test all cameras** on an appliance with several, to see each view before a case. For privacy the preview only works on the appliance itself, so open the portal there. It is unavailable while a recording is running, so a preview can never interrupt a recording.',
  },
  {
    id: 'clip-network-drop',
    section: 'Cameras & edge appliances',
    audience: STAFF,
    q: 'What happens to a camera clip if the network drops?',
    a: "The video stays on the appliance, in the **recordings** folder next to it, and uploads in parts that pick up where they stopped once the network is back — nothing is lost. The appliance shows how many videos are still waiting, and the session's **Video** step shows the progress.",
    more: "If the server turns a video down for good, the appliance stops retrying it and moves it to a **failed** folder, and the session's **Video** step says so — contact us rather than deleting it. During a recording the appliance also warns when its disk is running low.",
  },
  {
    id: 'camera-froze',
    section: 'Cameras & edge appliances',
    audience: STAFF,
    q: 'What does "stopped sending video" mean?',
    a: "A camera stopped delivering pictures in the middle of a recording — usually a loose USB cable or a USB hub. The session's tile on the **Live** page turns red within a few seconds, and the appliance's own window prints a warning, so someone can reseat the cable while the case is still running. Afterwards the session's **Video** step says where the video stopped.",
    more: 'Plug cameras straight into the appliance rather than through a hub.',
  },
  {
    id: 'missing-video',
    section: 'Cameras & edge appliances',
    audience: STAFF,
    q: "A session's camera video is missing — what do I check?",
    a: "Look at the session's **Video** step first: it names a camera that recorded nothing or an upload that keeps failing. Then check that the operator selected that appliance and camera, that the appliance was online, and that its clock is correct. A camera problem never affects the sensor recording.",
  },

  // ── Tenant administration ──────────────────────────────────────────────
  {
    id: 'invite-admins',
    section: 'Tenant administration',
    audience: ADMIN,
    q: 'How do I add another tenant admin or coordinator?',
    a: "In **Settings → Admins**, click **Invite** and choose Admin, Coordinator or Viewer (read-only). The same page switches an existing person between admin and coordinator, and suspends or re-enables them. You cannot change your own access, and a tenant always keeps at least one full admin.",
  },
  {
    id: 'read-only-admin',
    section: 'Tenant administration',
    audience: ADMIN,
    q: 'What is a read-only admin?',
    a: "Someone who sees everything in the tenant but cannot change, upload or download anything — suited to an observer or auditor. Grant it when inviting (choose **Viewer**), or with the **Access** switch on their row in **Settings → Admins**.",
  },
  {
    id: 'recording-roles',
    section: 'Recording a session',
    audience: TENANT_ALL,
    q: 'How do roles work in a simulation?',
    a: "When your organisation turns roles on (**Settings → Recording**), the capture app asks for a role, such as Primary nurse or Secondary nurse, and a procedure for every recording. After a recording, **Next role** goes straight to the next one with the same participant and sensors, without registering them again. The role shows on the session and in its reports.",
    more: 'Admins add, rename, reorder and remove roles in **Settings → Recording**. A role that recordings already used is archived instead of deleted: those recordings keep it, and the app stops offering it.',
  },
  {
    id: 'ready-then-go',
    section: 'Recording a session',
    audience: TENANT_ALL,
    q: 'What are standby and Go?',
    a: "Teams often start the recording, then scrub in and run the checks before the procedure really begins. With **Ready, then Go** (**Settings → Recording**), Ready starts the sensors, cameras and room audio, and **Go** marks where the procedure begins. The minutes before Go are kept as **standby**: greyed on the trace and left out of the scores, duration and reports. In a simulation Go is a button on the phone; in the operating room the phone listens for a phrase your organisation chose.",
    more: "In the operating room nobody has to touch the phone: it listens offline, only for the phrase, and confirms with a tone when it hears it said clearly. Pick at least two everyday English words that are said only when the case starts; the portal refuses words the phone couldn't recognise. Go can also be pressed on the **Live** page, for a recording waiting on standby. If Go was missed or came at the wrong moment, staff can fix it afterwards: click the moment on the session's cognitive trace and choose **Set Go here** (or **Clear standby** to count the whole recording). This works for any recording, also one made with a one-step start. The session is then re-scored and its transcript made again, which takes a few minutes.",
  },
  {
    id: 'metric-visibility',
    section: 'Tenant administration',
    audience: ADMIN,
    q: 'Can I choose which metrics appear on the scorecard?',
    a: "Yes — in **Settings → Metrics**, untick any metric to hide it everywhere the scorecard appears, including the capture app. The EEG choices also decide which lines the **Cognitive trace** offers. **Stress** and **Fatigue** are marked *cognitive trace only*: they have no scorecard row, so hiding them only removes them from the trace. Use **Applies to** to set different metrics for one procedure; **Reset to tenant default** removes that override. New metrics are shown until you hide them.",
  },
  {
    id: 'compare-settings',
    section: 'Tenant administration',
    audience: ADMIN,
    q: 'How do I turn Benchmark on or off?',
    a: "In **Settings → Compare**. One switch controls the staff **Benchmark** tab in Analysis; the other gives participants an anonymous comparison with their cohort there. By default staff have it and participants do not.",
  },
  {
    id: 'departments',
    section: 'Tenant administration',
    audience: ADMIN,
    q: 'What are departments and facilities for?',
    a: "They describe where work happens. A department is a broad group such as General Surgery; a facility is a room — an OR, a simulation lab, a classroom — optionally within a department. Set them up on **Departments & Facilities**, then tag recordings and devices with them.",
  },
  {
    id: 'procedures',
    section: 'Tenant administration',
    audience: ADMIN,
    q: 'How do procedures work?',
    a: "Each procedure has a unique name (capitals and spaces are ignored) and a number assigned automatically. Deleting a procedure hides it from the capture app but keeps it on past recordings, and frees its name for reuse.",
  },
  {
    id: 'cohort-baseline',
    section: 'Tenant administration',
    audience: ADMIN,
    q: "How do I create reference values for a procedure that doesn't have any?",
    a: "Use **Settings → Cohort baseline**. Pick a procedure, narrow the group by job title, experience or participant, preview the values, choose the metrics and the level (Novice, Practice-ready or Expert), and submit. 10NEWTONS reviews it; once approved, it appears as reference marks. While it waits, your Dashboard shows it as a provisional card.",
    more: 'Ten or more sessions give a steadier reference; smaller groups are allowed with a warning. Existing reference values are never overwritten.',
  },
  {
    id: 'data-integrity',
    section: 'Tenant administration',
    audience: STAFF,
    q: 'What is the Data integrity page?',
    a: "A check of your organisation's recordings for anything that did not arrive or did not finish processing, with who to ask about each. If it is empty, everything is in.",
  },
  {
    id: 'messages',
    section: 'Tenant administration',
    audience: ADMIN,
    q: 'What is in Settings → Messages?',
    a: "Notices from 10NEWTONS about your organisation — for example, that a capture-app crash on one of your phones was received and is being looked at. It is read-only.",
  },
  {
    id: 'tenant-audit-log',
    section: 'Tenant administration',
    audience: ADMIN,
    q: 'What is in Settings → Audit log?',
    a: "A record of deliberate actions in your organisation — who invited, edited, disabled or removed whom, and when — so you can answer \"who changed this?\". It is read-only.",
  },
  {
    id: 'email-delivery',
    section: 'Tenant administration',
    audience: ADMIN,
    q: 'What does the Email delivery card show?',
    a: "The emails your organisation sent in the last 30 days — invitations, welcome links and session-ready notices — and how many were delivered, bounced or failed. A spike in bounces usually means a batch of mistyped or blocked addresses.",
  },
  {
    id: 'contact-details-tenant',
    section: 'Tenant administration',
    audience: ADMIN,
    q: "How do I update my organisation's contact details?",
    a: "In **Settings**, open the **Contact** or **Address** tab and click **Edit details**.",
  },
  {
    id: 'tenant-sso-check',
    section: 'Tenant administration',
    audience: ADMIN,
    q: 'Does my organisation use single sign-on?',
    a: "See **Settings → Single sign-on**: it shows your connection, the email domains it covers and the role new users get — or says plainly that none is set up. Setting it up or changing it is done by 10NEWTONS; contact us.",
  },
  {
    id: 'tenant-mfa',
    section: 'Tenant administration',
    audience: ADMIN,
    q: 'How do I require two-factor sign-in for our admins?',
    a: "Turn on **Require MFA for tenant admins** in **Settings → Security**. Every admin sets it up at their next sign-in and cannot turn it off themselves. It is on by default.",
  },
  {
    id: 'trial',
    section: 'Tenant administration',
    audience: ADMIN,
    q: 'What does a trial license mean?',
    a: "Your organisation is on a trial license; **Settings → License** shows when it ends, and the Dashboard warns you in time. When it ends the organisation becomes read-only — everyone can still sign in and see their data — until 10NEWTONS issues a license. Contact us before then.",
  },
  {
    id: 'license',
    section: 'Tenant administration',
    audience: ADMIN,
    q: 'What does our license cover, and when does it end?',
    a: "**Settings → License** shows your license key, its end date and how many participants, devices and procedures you use against its limits. Admins are emailed 45, 30, 14, 7 and 1 days before the end date, and the Dashboard shows a warning. Your 10NEWTONS representative renews it.",
    more: "Participants count while their access is on, so disabling people who have finished frees their seats; devices count until they are retired; procedures count from the start of the current license term, so renewing starts the count again. Staff accounts don't count. When the procedures are used up, no new procedures can be started until the limit is raised; everything already captured is kept. An organisation run under another's license — a conference run by a research group, for example — has its own key but shares the main license's dates and limits, and its admins get the same reminders.",
  },
  {
    id: 'license-ended',
    section: 'Tenant administration',
    audience: TENANT_ALL,
    q: 'What happens when our license ends?',
    a: "Everything keeps working for 14 days after the end date (a trial has no extra days). After that the organisation is read-only: everyone can still sign in, see and export their data, but new recordings, enrollments, devices and uploads are blocked. Renewing restores everything at once.",
  },
  {
    id: 'suspended',
    section: 'Tenant administration',
    audience: TENANT_ALL,
    q: 'What happens when my organisation is suspended?',
    a: "You can still sign in, but that organisation's pages show a suspension notice instead of data, and its capture phones cannot upload. Any other organisation you belong to keeps working. Contact 10NEWTONS to reactivate it.",
  },

  // ── Platform administration ────────────────────────────────────────────
  {
    id: 'publish-terms',
    section: 'Platform administration',
    audience: SUPER,
    q: 'How do I publish or update the Terms of Use and Privacy Policy?',
    a: 'In **Configuration → Terms & privacy**, click **New version**: it starts from the latest text, written in Markdown with a **Preview** tab. **Publish** puts it live. Until the first version is published, nobody is asked to accept anything.',
    more: "A published version can't be edited or deleted, so every acceptance points to the exact text agreed to. Turn on **Material change** to ask every signed-in user to accept again; leave it off for small corrections. An **Effective from** date in the future shows the new version on the public page as upcoming until that date. **Accepted by** counts who has accepted each version.",
  },
  {
    id: 'min-app-version',
    section: 'Platform administration',
    audience: SUPER,
    q: 'How do I make every phone install a capture-app update?',
    a: 'On **Capture-app releases**, set **Minimum supported version** to the release phones must run. Phones below it show a red **Update required** banner and can\'t start a recording until they install the current release. Leave it at **None** for routine releases — phones then only see an update notice.',
    more: 'You can only choose a release that has been uploaded and is no newer than the current one, so phones are never told to install a build they can\'t get. A phone that is offline can\'t check, so it keeps recording until it is back online.',
  },
  {
    id: 'platform-admin-tenant-pages',
    section: 'Platform administration',
    audience: SUPER,
    q: "Why can't I open a tenant's pages as a platform admin?",
    a: "A platform admin doesn't see a tenant's own data directly. Open the tenant on **Tenants** and start a support session from its page; you then work as one of its admins, and the session is recorded. Use a separate account if you also run a tenant yourself.",
  },
  {
    id: 'tenant-cards',
    section: 'Platform administration',
    audience: SUPER,
    q: 'What do the numbers on each tenant card mean?',
    a: 'The first row counts participants (total, active, suspended); the second counts procedures and devices in service and all recordings. Click a card to open the tenant.',
  },
  {
    id: 'disable-delete-tenant',
    section: 'Platform administration',
    audience: SUPER,
    q: 'What is the difference between disabling and deleting a tenant?',
    a: "Disabling (the **Active** switch) suspends the tenant and can be undone with a click. Deleting hides it from lists but keeps its data and reserves its name. Permanently erasing a tenant is not available in the portal.",
  },
  {
    id: 'trials',
    section: 'Platform administration',
    audience: SUPER,
    q: 'How do tenant trials work?',
    a: "When creating a tenant, pick 7, 14 or 21 days under **Trial license** (or **None** to issue a license later): that issues a trial license, listed on **Licenses**. Its admins are warned before it ends, and at the end the tenant becomes read-only, not suspended. To continue, renew it from the row menu — it becomes a standard license with the same key.",
  },
  {
    id: 'issue-license',
    section: 'Platform administration',
    audience: SUPER,
    q: 'How do I issue or renew a license?',
    a: "On **Licenses**, click **Issue license**: pick the organisation, the term (3 to 36 months, or a 7, 14 or 21-day trial) and optional participant, device and procedure limits. The row menu renews, changes limits, adds a sub-license for another organisation, shows the history, or revokes. Notes stay internal.",
    more: "A sub-license gives another organisation its own key while it follows the main license: same dates and state, and its participants and devices count towards the main license's limits. Revoking a license makes the organisation read-only at once — not unlicensed — and revoking a main license does the same to its sub-licenses; to restore one, choose **Issue new license** on its row. Platform admins are emailed when a license goes over a limit; organisations without a license are listed as Unlicensed and are not limited.",
  },
  {
    id: 'tenant-license-status',
    section: 'Platform administration',
    audience: SUPER,
    q: 'What does the license chip on the Tenants list mean?',
    a: "**Licensed**: more than 45 days to go. **Ends in N days**: inside the warning window — its admins are being reminded. **Trial: N days left**: a trial license. **Ended — grace**: past the end date but still working, for 14 days. **Read-only** / **Revoked — read-only**: past the grace period, or revoked; people can still sign in and view, but nothing new is recorded or enrolled. **Unlicensed**: never had a license, so nothing is limited. An organisation on a sub-license shows its main license's status.",
  },
  {
    id: 'first-admin',
    section: 'Platform administration',
    audience: SUPER,
    q: 'I created a tenant without an admin — how do I add one?',
    a: "Open the tenant, click **Edit tenant** and enter the admin's email; they receive the first-admin invitation. The field is only editable while the tenant has no active admin — after that, admins manage their own team in **Settings → Admins**.",
  },
  {
    id: 'platform-admins',
    section: 'Platform administration',
    audience: SUPER,
    q: 'How do I add or suspend a 10NEWTONS administrator?',
    a: "In **Access → Platform admins**: **Invite admin** sends an invitation; the **Active** switch suspends or re-enables someone. Pending invitations can be resent or cancelled below the list. You cannot suspend yourself.",
  },
  {
    id: 'access-requests',
    section: 'Platform administration',
    audience: SUPER,
    q: 'Someone signed up but sees "Access pending" — how do I let them in?',
    a: "Open **Access → Access requests**, click **Grant access** and choose their role and tenant. They get in the next time they sign in or click **Check again**. **Dismiss** permanently deletes an unwanted sign-up.",
    more: 'The list is everyone signed in without an active role, with the IP address they registered from. Someone whose only membership was suspended appears here again; a removed participant does not. Every grant and dismissal is recorded in the audit trail.',
  },
  {
    id: 'platform-mfa',
    section: 'Platform administration',
    audience: SUPER,
    q: 'Where is the two-factor policy for platform admins?',
    a: "**Configuration → MFA policy**. When on (the default), every platform admin must set up two-factor at their next sign-in and cannot turn it off.",
  },
  {
    id: 'sso-setup',
    section: 'Platform administration',
    audience: SUPER,
    q: 'How do I set up single sign-on for a tenant?',
    a: "**Configuration → Enterprise SSO**: pick the tenant, paste the connection ID from the identity broker, list the email domains it covers, and choose the role new users receive. **Enforce SSO** hides the password option for those domains; **SSO satisfies MFA** skips our code step. The banner at the top says whether the broker itself is configured — without it, everyone signs in with a password.",
    more: 'Each email domain can belong to only one tenant. Removing a connection leaves existing accounts working with passwords.',
  },
  {
    id: 'mail-settings',
    section: 'Platform administration',
    audience: SUPER,
    q: 'Where are the mail settings?',
    a: "**Configuration → Mail**. Choose SMTP or Mailgun and fill in its details; changes apply to the next email. Save before using **Send a test email**, which uses the saved settings. Secrets are never shown again after saving.",
    more: 'For Mailgun, the webhook signing key is what enables delivered / bounced counts. A new Mailgun account only sends to authorised test addresses until its sending domain is verified and billing is set up.',
  },
  {
    id: 'ai-insights',
    section: 'Platform administration',
    audience: SUPER,
    q: 'What does Configuration → AI / Insights control?',
    a: "Transcripts, the AI Coach and badge reading. Enter the **OpenAI API key**, switch on **Transcribe anchor audio** for transcripts, pick the **Chat model**, and switch on **Enable AI Coach narration**. Room audio from clinical settings is sent to OpenAI, so turn transcripts on only once your data-processing agreements allow it.",
    more: "Enter the chat model's **Input price** and **Output price** (per million tokens) so the AI spend card can show what the AI costs. If the account runs out of credit, transcripts and AI Coach work pause and pick up again by themselves once credit is added.",
  },
  {
    id: 'system-status',
    section: 'Platform administration',
    audience: SUPER,
    q: 'What does Operations → System status show?',
    a: "Whether the API, the database and each processing worker are healthy right now, with a 7-day timeline for each. **Operations → Workers** shows scoring speed and the queue — if nothing is completing while recordings arrive, look there first.",
  },
  {
    id: 'logs-vs-audit',
    section: 'Platform administration',
    audience: SUPER,
    q: 'What is the difference between System logs and the Audit trail?',
    a: "**Diagnostics → System logs** is the application's running commentary, for troubleshooting. **Diagnostics → Audit trail** records deliberate actions — who did what, and when — including failed sign-ins with their IP address.",
  },
  {
    id: 'crash-reports',
    section: 'Platform administration',
    audience: SUPER,
    q: 'How are capture-app crashes reported?',
    a: "The capture app saves a crash and uploads it the next time it starts. Platform admins get an email with the full technical report; the tenant's admins get a plain-language note that a recording may need redoing. **Diagnostics → Crash reports** lists them — opening one tells the tenant it is being looked at.",
  },
  {
    id: 'reset-alert',
    section: 'Platform administration',
    audience: SUPER,
    q: 'Is anyone told when a platform admin resets their password?',
    a: "Yes — every other platform admin is emailed, with the time and IP address. The same applies to a sign-up still waiting for access. If it was not them, suspend the account and check the audit trail.",
  },
  {
    id: 'master-baselines',
    section: 'Platform administration',
    audience: SUPER,
    q: 'How do I manage reference values?',
    a: "**Library → Catalogue** holds every reference value; export it as CSV from the toolbar. **Library → Submissions** is where tenants' own reference values wait for review: approving adds them to the catalogue without overwriting existing values; rejecting records a reason.",
  },
  {
    id: 'fleet-devices',
    section: 'Platform administration',
    audience: SUPER,
    q: 'The same phone shows different app versions in two tenants — which is right?',
    a: "**Operations → Devices** lists every enrollment across tenants with each phone's latest app version. A tenant's own Devices page only updates when the phone last spoke to that tenant, so it can lag.",
  },
];
